import { Check, Clear } from '@mui/icons-material'
import { Box, Button, Checkbox, FormControlLabel, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useGetDictionariesQuery } from '../../store/redux/reducers/dictionarySlice'
import EmploymentType from '../../store/types/enums/EmploymentType'
import { SettlementMethod } from '../../store/types/enums/SettlementMethod'
import { EmploymentFormValues } from '../../store/types/form/EmploymentFormValues'
import { FormVacationPeriod } from '../../store/types/form/FormVacationPeriod'
import { ContractLightDTO } from '../../store/types/models/Contract'
import { hasInvalidEmploymentRange, mapVacationPeriodsToFormValues } from '../../store/utils/employmentUtils'
import { sortVacationPeriodsByDate } from '../../store/utils/sortingFunctions'
import { StyledContainerColumnLeft } from '../../styles/ContainerStyles'
import { getGap } from '../../styles/stylePropsFunctions'
import DiscardChangesDialog from '../Dialog/DiscardChangesDialog'
import EmploymentFormDialogTitle from '../Dialog/EmploymentFormDialog/EmploymentFormDialogTitle'
import EmploymentFormVacationPeriodsSection from '../Employment/EmploymentFormVacationPeriodsSection'
import AutocompleteEmploymentTypes from '../Page/AutocompleteEmploymentType'
import AutocompleteSettlementTypes from '../Page/AutocompleteSettlementTypes'
import DateInput from './Inputs/DateInput'
import NumericInput from './Inputs/NumericInput'

type EmploymentFormProps = {
  onClose: () => void
  onSubmit: (values: EmploymentFormValues, allowNavigation: () => void) => Promise<void>
  isNewEmployment?: boolean
  contract?: ContractLightDTO
  employeeName?: string
}

const EmploymentForm = ({
  onClose,
  onSubmit: onFormSubmit,
  isNewEmployment = false,
  contract,
  employeeName,
}: EmploymentFormProps) => {
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false)
  const allowNavigationRef = useRef<boolean>(false)
  const [storedVacationPeriods, setStoredVacationPeriods] = useState<FormVacationPeriod[]>([])
  const [storedEndDate, setStoredEndDate] = useState<dayjs.Dayjs | null>(null)

  const { data: options } = useGetDictionariesQuery({
    page: 0,
    size: 20,
    group: 'EMPLOYMENT_TYPE',
  })

  const employmentType: EmploymentType = useMemo(() => {
    if (!contract || !options) return EmploymentType.B2B
    return options.content.find(o => o.value === contract?.contractTypeValue)?.key as EmploymentType
  }, [contract, options])

  const vacationPeriods: FormVacationPeriod[] = useMemo(() => {
    if (!contract) return []
    const mappedPeriods = mapVacationPeriodsToFormValues(contract.paidVacationPeriods ?? [])
    return sortVacationPeriodsByDate(mappedPeriods)
  }, [contract])

  const defaultFormValues: EmploymentFormValues = useMemo(() => {
    const start = contract
      ? contract.startDate
        ? dayjs(contract.startDate).utc(true).startOf('day')
        : dayjs()
      : dayjs()

    const end = contract
      ? contract.endDate
        ? dayjs(contract.endDate).utc(true).startOf('day')
        : start.add(1, 'day')
      : start.add(1, 'day')

    const isIndefiniteDuration =
      !!contract && (contract.indefiniteDuration === true || contract.endDate === null || contract.endDate === '')

    return {
      employmentType: employmentType,
      settlementMethod: contract ? contract.settlementMethod : SettlementMethod.FLAT,
      startDate: start,
      endDate: isIndefiniteDuration ? null : end,
      indefiniteDuration: isIndefiniteDuration,
      employmentPercentage: contract ? contract.employmentPercentage : 100,
      paidVacation: contract ? contract.paidVacation : false,
      vacationPeriods: vacationPeriods,
    }
  }, [employmentType, contract, vacationPeriods])

  const formMethods = useForm<EmploymentFormValues>({
    defaultValues: defaultFormValues,
    mode: 'all',
  })

  const { control, handleSubmit, watch, formState, setValue, trigger } = formMethods
  const contractStart = watch('startDate')
  const contractEnd = watch('endDate')
  const indefiniteDuration = watch('indefiniteDuration')
  const currentEmploymentType = watch('employmentType')
  const settlementMethod = watch('settlementMethod')
  const paidVacation = watch('paidVacation')
  const currentVacationPeriods = watch('vacationPeriods')

  const onSubmit = async (values: EmploymentFormValues) => {
    const shouldDisableVacationPeriods = !isPaidVacationAllowed || !values.paidVacation
    const submitValues = {
      ...values,
      vacationPeriods: shouldDisableVacationPeriods ? [] : values.vacationPeriods,
      paidVacation: shouldDisableVacationPeriods ? false : values.paidVacation,
    }
    await onFormSubmit(submitValues, () => {
      allowNavigationRef.current = true
    })
  }

  const isConfirmButtonDisabled = useMemo(() => {
    return (
      !formState.isValid ||
      (!isNewEmployment && !formState.isDirty) ||
      hasInvalidEmploymentRange(contractStart, contractEnd)
    )
  }, [formState.isValid, formState.isDirty, isNewEmployment, contractStart, contractEnd])

  const isPaidVacationAllowed = useMemo(() => {
    return (
      settlementMethod !== SettlementMethod.HOURLY &&
      currentEmploymentType !== EmploymentType.MANDATE_CONTRACT &&
      currentEmploymentType !== EmploymentType.WORK_CONTRACT
    )
  }, [settlementMethod, currentEmploymentType])

  useEffect(() => {
    trigger('vacationPeriods')
  }, [contractStart, contractEnd, currentVacationPeriods, trigger])

  useEffect(() => {
    const shouldDisableVacationPeriods = !isPaidVacationAllowed || !paidVacation

    if (shouldDisableVacationPeriods) {
      if (currentVacationPeriods.length > 0) {
        setStoredVacationPeriods(currentVacationPeriods)
        setValue('vacationPeriods', [], { shouldDirty: true })
      }
      return
    }

    if (currentVacationPeriods.length === 0 && storedVacationPeriods.length > 0) {
      setValue('vacationPeriods', storedVacationPeriods, { shouldDirty: true })
    }
  }, [paidVacation, isPaidVacationAllowed, setValue, storedVacationPeriods, currentVacationPeriods])

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate aria-labelledby="generate-report-dialog-title">
          <StyledContainerColumnLeft
            sx={{
              p: 4,
              gap: getGap(isDownSm),
            }}
          >
            <EmploymentFormDialogTitle isNewEmployment={isNewEmployment} employeeName={employeeName} />

            <Stack
              flexDirection={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              flexWrap={'wrap'}
              gap={2}
            >
              <Typography component={'h4'} variant={'h5'} color="text.highEmphasis">
                {t('page.usersProfile.tiles.employment.contractData')}
              </Typography>
            </Stack>

            <Stack sx={{ gap: 2 }} maxWidth={500}>
              <AutocompleteEmploymentTypes
                value={watch('employmentType')}
                onChange={val => setValue('employmentType', val as EmploymentType, { shouldDirty: true })}
              />
              <DateInput
                control={control}
                name="startDate"
                label={t('page.employments.table.startDate')}
                rules={{
                  validate: {
                    employmentRange: v => {
                      if (!dayjs.isDayjs(v)) return false
                      if (watch('indefiniteDuration')) return true
                      const end = watch('endDate')
                      return dayjs(v).isBefore(dayjs(end))
                    },
                  },
                }}
                onValueChange={() => trigger('endDate')}
              />

              <FormControlLabel
                sx={{ m: 0, width: 'fit-content' }}
                control={
                  <Checkbox
                    checked={!!indefiniteDuration}
                    onChange={(_, value) => {
                      if (value) {
                        const currentEnd = contractEnd
                        if (currentEnd !== null) {
                          setStoredEndDate(currentEnd)
                        }
                        setValue('indefiniteDuration', true, { shouldDirty: true, shouldValidate: true })
                        setValue('endDate', null, { shouldDirty: true, shouldValidate: true })
                      } else {
                        const fallback =
                          storedEndDate ?? (contractStart ? dayjs(contractStart).add(1, 'day') : dayjs().add(1, 'day'))

                        setValue('indefiniteDuration', false, { shouldDirty: true, shouldValidate: true })
                        setValue('endDate', fallback, { shouldDirty: true, shouldValidate: true })
                      }

                      trigger(['startDate', 'endDate', 'vacationPeriods'])
                    }}
                  />
                }
                label={t('page.employments.table.indefiniteDurationShort')}
              />

              {!indefiniteDuration && (
                <DateInput
                  control={control}
                  name="endDate"
                  label={t('page.employments.table.endDate')}
                  rules={{
                    validate: {
                      employmentRange: v => {
                        if (!dayjs.isDayjs(v)) return false
                        const start = watch('startDate')
                        return dayjs(start).isBefore(dayjs(v))
                      },
                    },
                  }}
                  onValueChange={() => trigger('startDate')}
                />
              )}
              <AutocompleteSettlementTypes
                value={watch('settlementMethod')}
                onChange={val => setValue('settlementMethod', val as SettlementMethod, { shouldDirty: true })}
              />
              <NumericInput
                control={control}
                name="employmentPercentage"
                label={t('page.employments.table.employmentPercentage')}
                numericType="natural"
                type="number"
                minValue={0}
                maxValue={100}
                unitOfMeasure="%"
                showSpinButtons
                rules={{
                  min: 0,
                  max: 100,
                }}
                sxProps={{
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  '& input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }}
              />

              {isPaidVacationAllowed && (
                <FormControlLabel
                  sx={{ m: 0, width: 'fit-content' }}
                  control={
                    <Checkbox
                      checked={paidVacation}
                      onChange={(_, value) => setValue('paidVacation', value, { shouldDirty: true })}
                    />
                  }
                  label={t('page.employments.table.paidVacation')}
                />
              )}
            </Stack>

            {isPaidVacationAllowed && paidVacation && (
              <EmploymentFormVacationPeriodsSection contractStart={contractStart} contractEnd={contractEnd} />
            )}

            <Box ml={{ xs: 'none', sm: 'auto' }}>
              <Stack flexDirection={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', md: 'auto' }} gap={2}>
                <Button
                  type="button"
                  size="small"
                  variant="contained"
                  color="secondary"
                  fullWidth={isDownSm}
                  startIcon={<Clear />}
                  onClick={onClose}
                >
                  {t('page.schedule.cancel')}
                </Button>
                <Button
                  type="submit"
                  size="small"
                  variant="contained"
                  color="primary"
                  fullWidth={isDownSm}
                  startIcon={<Check />}
                  disabled={isConfirmButtonDisabled}
                >
                  {isNewEmployment ? t('components.button.saveContract') : t('page.schedule.save')}
                </Button>
              </Stack>
            </Box>
          </StyledContainerColumnLeft>
        </form>
      </FormProvider>

      <DiscardChangesDialog
        open={isCancelDialogOpen}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
        checkUnsavedChanges={() => !allowNavigationRef.current && formState.isDirty}
        disableBlocker={allowNavigationRef.current}
      />
    </>
  )
}

export default EmploymentForm
